import { getCurrentOperatorName } from './currentOperator';

export type OperatorCommandGroup = 'Navigation' | 'Trade Entry' | 'Workflow' | 'Search';

export interface OperatorCommandAction {
  route: string;
  state?: Record<string, any>;
  sessionPrefill?: Record<string, any>;
}

export interface OperatorCommandDefinition extends OperatorCommandAction {
  id: string;
  title: string;
  subtitle: string;
  group: OperatorCommandGroup;
  aliases: string[];
  shortcut?: string;
  example?: string;
}

export interface OperatorCommandMatch extends OperatorCommandDefinition {
  source: 'command';
  score: number;
}

export interface ShortcutHelpItem {
  keys: string[];
  action: string;
  note?: string;
}

export interface ShortcutHelpGroup {
  title: string;
  items: ShortcutHelpItem[];
}

const normalize = (value: string) =>
  value
    .trim()
    .replace(/\s+/g, ' ')
    .toUpperCase();

const encodeQuery = (value: string) => encodeURIComponent(value.trim());

const ticketCommands: OperatorCommandDefinition[] = [
  {
    id: 'ticket-fi',
    title: 'Fixed Income Ticket',
    subtitle: 'Corporate, sovereign, muni, CLO debt, and other liquid credit trades',
    group: 'Trade Entry',
    aliases: ['FI', 'FIXED INCOME', 'BOND', 'CORP'],
    shortcut: 'n f',
    route: '/trade-entry/fixed-income',
    example: 'FI',
  },
  {
    id: 'ticket-equity',
    title: 'Equity Ticket',
    subtitle: 'Listed equity trade entry',
    group: 'Trade Entry',
    aliases: ['EQ', 'EQUITY', 'STOCK'],
    shortcut: 'n e',
    route: '/trade-entry/equity',
    example: 'EQ',
  },
  {
    id: 'ticket-leveraged-loan',
    title: 'Leveraged Loan Ticket',
    subtitle: 'Syndicated loan assignment / participation workflow',
    group: 'Trade Entry',
    aliases: ['LL', 'LOAN', 'LEVERAGED LOAN', 'SYNDICATED LOAN', 'LSTA', 'TLA', 'TLB'],
    shortcut: 'n l',
    route: '/trade-entry/leveraged-loan',
    example: 'LL',
  },
  {
    id: 'ticket-private-credit',
    title: 'Direct Lending Ticket',
    subtitle: 'Direct lending and bilateral private credit acquisition workflow',
    group: 'Trade Entry',
    aliases: ['PC', 'PRIVATE CREDIT', 'PRIVATE FIXED INCOME', 'DIRECT LENDING', 'BILATERAL LOAN'],
    shortcut: 'n p',
    route: '/trade-entry/private-fixed-income',
    example: 'DL',
  },
  {
    id: 'ticket-abf',
    title: 'ABF Facility Ticket',
    subtitle: 'Asset-backed finance facility trade entry',
    group: 'Trade Entry',
    aliases: ['ABF', 'ABF FACILITY'],
    route: '/trade-entry/abf-facility',
    example: 'ABF',
  },
  {
    id: 'ticket-rmbs',
    title: 'RMBS Tranche Ticket',
    subtitle: 'Residential mortgage-backed security tranche',
    group: 'Trade Entry',
    aliases: ['RMBS', 'RMBS TRANCHE'],
    route: '/trade-entry/rmbs-tranche',
    example: 'RMBS',
  },
  {
    id: 'ticket-abs',
    title: 'ABS Tranche Ticket',
    subtitle: 'Consumer and commercial asset-backed security tranche',
    group: 'Trade Entry',
    aliases: ['ABS', 'ABS TRANCHE', 'ASSET BACKED SECURITY'],
    route: '/trade-entry/abs-tranche',
    example: 'ABS',
  },
  {
    id: 'ticket-cmbs',
    title: 'CMBS Tranche Ticket',
    subtitle: 'Commercial mortgage-backed security tranche',
    group: 'Trade Entry',
    aliases: ['CMBS', 'CMBS TRANCHE'],
    route: '/trade-entry/cmbs-tranche',
    example: 'CMBS',
  },
  {
    id: 'ticket-clo',
    title: 'CLO Equity Ticket',
    subtitle: 'CLO equity acquisition workflow',
    group: 'Trade Entry',
    aliases: ['CLO', 'CLO EQUITY'],
    route: '/trade-entry/clo-equity',
    example: 'CLO',
  },
  {
    id: 'ticket-msr',
    title: 'MSR Ticket',
    subtitle: 'Mortgage servicing rights workflow',
    group: 'Trade Entry',
    aliases: ['MSR', 'MORTGAGE SERVICING RIGHTS'],
    route: '/trade-entry/msr',
    example: 'MSR',
  },
  {
    id: 'ticket-irs',
    title: 'Interest Rate Swap Ticket',
    subtitle: 'Receive/pay fixed and floating swap legs',
    group: 'Trade Entry',
    aliases: ['IRS', 'IR', 'IR SWAP', 'INTEREST RATE SWAP', 'SWAP'],
    shortcut: 'n i',
    route: '/trade-entry/derivatives/ir-swap',
    example: 'IRS',
  },
  {
    id: 'ticket-trs',
    title: 'Total Return Swap Ticket',
    subtitle: 'Total return and financing leg workflow',
    group: 'Trade Entry',
    aliases: ['TRS', 'TOTAL RETURN SWAP'],
    route: '/trade-entry/derivatives/tr-swap',
    example: 'TRS',
  },
  {
    id: 'ticket-cds',
    title: 'Credit Default Swap Ticket',
    subtitle: 'Single-name and index protection workflow',
    group: 'Trade Entry',
    aliases: ['CDS', 'CD SWAP', 'CREDIT DEFAULT SWAP', 'CDX'],
    route: '/trade-entry/derivatives/cd-swap',
    example: 'CDS',
  },
  {
    id: 'ticket-swaption',
    title: 'Swaption Ticket',
    subtitle: 'Option on underlying interest rate swap',
    group: 'Trade Entry',
    aliases: ['SWPT', 'SWAPTION'],
    route: '/trade-entry/derivatives/swaption',
    example: 'SWPT',
  },
  {
    id: 'ticket-futures',
    title: 'Futures Ticket',
    subtitle: 'Exchange-traded futures workflow',
    group: 'Trade Entry',
    aliases: ['FUT', 'FUTURES', 'FUTURE'],
    route: '/trade-entry/derivatives/futures',
    example: 'FUT',
  },
  {
    id: 'ticket-options',
    title: 'Options Ticket',
    subtitle: 'Exchange-traded listed options workflow',
    group: 'Trade Entry',
    aliases: ['OPT', 'OPTION', 'OPTIONS'],
    route: '/trade-entry/derivatives/options',
    example: 'OPT',
  },
  {
    id: 'ticket-fx',
    title: 'FX Ticket',
    subtitle: 'Spot, forward, swap, and NDF workflow',
    group: 'Trade Entry',
    aliases: ['FX', 'FX SPOT', 'FOREX'],
    shortcut: 'n x',
    route: '/trade-entry/fx',
    example: 'FX',
  },
  {
    id: 'ticket-repo',
    title: 'Repo Ticket',
    subtitle: 'Repo and reverse repo workflow',
    group: 'Trade Entry',
    aliases: ['REPO', 'RP'],
    shortcut: 'n r',
    route: '/trade-entry/repo',
    example: 'REPO',
  },
  {
    id: 'ticket-cash',
    title: 'Cash Transfer Ticket',
    subtitle: 'Cash movement workflow',
    group: 'Trade Entry',
    aliases: ['CASH TKT', 'CASH TRANSFER'],
    route: '/trade-entry/cash',
    example: 'CASH TKT',
  },
];

export const operatorCommands: OperatorCommandDefinition[] = [
  {
    id: 'nav-dashboard',
    title: 'Dashboard',
    subtitle: 'Open portfolio dashboard',
    group: 'Navigation',
    aliases: ['D', 'DASH', 'DASHBOARD', 'HOME'],
    shortcut: 'g d',
    route: '/home',
  },
  {
    id: 'nav-positions',
    title: 'Positions',
    subtitle: 'Open IBOR positions / holdings',
    group: 'Navigation',
    aliases: ['POS', 'POSITION', 'POSITIONS', 'HOLDINGS'],
    shortcut: 'g p',
    route: '/holdings',
    example: 'POS VANGUARD',
  },
  {
    id: 'nav-blotter',
    title: 'Order Blotter',
    subtitle: 'Open trade blotter',
    group: 'Navigation',
    aliases: ['ORD', 'ORDER', 'ORDERS', 'BLOTTER'],
    shortcut: 'g b',
    route: '/bond-transactions',
    example: 'ORD TODAY',
  },
  {
    id: 'nav-trade-entry',
    title: 'Trade Entry',
    subtitle: 'Open the default fixed income ticket',
    group: 'Navigation',
    aliases: ['TKT', 'TICKET', 'TRADE', 'TRADE ENTRY'],
    shortcut: 'g t',
    route: '/trade-entry/fixed-income',
    example: 'TKT FI IBM 5MM BUY',
  },
  {
    id: 'nav-compliance',
    title: 'Compliance',
    subtitle: 'Open compliance dashboard',
    group: 'Navigation',
    aliases: ['COMP', 'COMPLIANCE'],
    shortcut: 'g c',
    route: '/compliance-dashboard',
    example: 'COMP BREACHES',
  },
  {
    id: 'nav-reports',
    title: 'Analytics & Reports',
    subtitle: 'Open analytics and reporting workspace',
    group: 'Navigation',
    aliases: ['RPT', 'REPORT', 'REPORTS', 'ANALYTICS'],
    shortcut: 'g r',
    route: '/analytics',
  },
  {
    id: 'nav-ai',
    title: 'AI Agents',
    subtitle: 'Open AI assistant and agent hub',
    group: 'Navigation',
    aliases: ['AI', 'COPILOT', 'AGENTS'],
    shortcut: 'g a',
    route: '/ai-agents',
  },
  {
    id: 'nav-document-processing',
    title: 'Document Processing',
    subtitle: 'Open the Middle Office document ingestion pipeline',
    group: 'Navigation',
    aliases: ['DOC', 'DOCUMENTS', 'DOCUMENT PROCESSING', 'INGESTION', 'MIDDLE OFFICE DOCUMENTS'],
    shortcut: 'g m',
    route: '/middle-office?tab=document-processing',
  },
  {
    id: 'nav-credit-loans',
    title: 'Credit & Loans',
    subtitle: 'Open the private credit and leveraged loan workspace',
    group: 'Navigation',
    aliases: ['LOANS', 'CREDIT LOANS', 'CREDIT & LOANS', 'CREDIT DESK'],
    shortcut: 'g l',
    route: '/portfolio-management?tab=credit-loans',
  },
  {
    id: 'nav-private-credit',
    title: 'Private Credit',
    subtitle: 'Private credit home: the facility board, the register and the work queue',
    group: 'Navigation',
    aliases: ['PC', 'PRIVATE CREDIT', 'DIRECT LENDING', 'PC LOANS', 'BILATERAL'],
    route: '/portfolio-management/credit/private-credit',
    example: 'PC',
  },
  {
    id: 'nav-leveraged-loans',
    title: 'Leveraged Loans',
    subtitle: 'Leveraged loan workbench: syndicated loan positions, settlement and lifecycle',
    group: 'Navigation',
    aliases: ['LL', 'LEVERAGED LOANS', 'LEVERAGED', 'SYNDICATED', 'BSL', 'TERM LOANS'],
    route: '/portfolio-management?tab=credit-loans&sub=loans',
    example: 'LL',
  },
  {
    id: 'nav-loan-document-reviews',
    title: 'Loan Document Reviews',
    subtitle: 'Private credit and syndicated loan documents to review against their source and import',
    group: 'Workflow',
    aliases: ['PC AGR', 'LL AGR', 'LOAN AGR', 'LOAN DOCS', 'LOAN REVIEW', 'PC REVIEW', 'LL REVIEW', 'PC DOCS', 'LL DOCS'],
    route: '/middle-office?tab=document-processing',
    example: 'PC AGR',
  },
  {
    id: 'nav-abf',
    title: 'ABF Portfolio',
    subtitle: 'Asset-based finance facilities, agreement queue and facility workspaces',
    group: 'Navigation',
    aliases: ['ABF', 'ABP', 'ABF PORTFOLIO', 'ASSET BASED FINANCE', 'ASSET-BASED FINANCE', 'FACILITIES'],
    route: '/portfolio-management?tab=credit-loans&sub=abf',
    example: 'ABF',
  },
  {
    id: 'nav-abf-agreements',
    title: 'ABF Agreement Queue',
    subtitle: 'Credit agreements and amendments awaiting review and activation',
    group: 'Workflow',
    aliases: ['AGR', 'AGREEMENTS', 'ABF AGR', 'ABF QUEUE', 'ABF Q', 'ACTIVATIONS', 'ABF REVIEW'],
    route: '/portfolio-management?tab=credit-loans&sub=abf',
    example: 'AGR',
  },
  {
    id: 'nav-alternatives',
    title: 'Alternatives',
    subtitle: 'Open alternatives, private equity, real estate, and fund workflows',
    group: 'Navigation',
    aliases: ['ALTS', 'ALTERNATIVES', 'PRIVATE EQUITY', 'REAL ESTATE', 'FUNDS'],
    shortcut: 'g v',
    route: '/portfolio-management?tab=alternatives',
  },
  {
    id: 'nav-settings',
    title: 'Settings',
    subtitle: 'Open user preferences and app settings',
    group: 'Navigation',
    aliases: ['SET', 'SETTINGS', 'PREF', 'PREFERENCES'],
    shortcut: 'g s',
    route: '/preferences',
  },
  {
    id: 'workflow-rfq',
    title: 'Start RFQ',
    subtitle: 'Open blotter in RFQ mode',
    group: 'Workflow',
    aliases: ['RFQ', 'QUOTE'],
    route: '/bond-transactions?action=rfq',
    example: 'RFQ IBM 2034',
  },
  {
    id: 'workflow-preclear',
    title: 'Trades Pending Pre-Clear',
    subtitle: 'Open draft trades ready for pre-clear submission',
    group: 'Workflow',
    aliases: ['PRECLEAR', 'PRE-CLEAR', 'SUBMIT PRECLEAR'],
    route: '/bond-transactions?status=DRAFT',
  },
  {
    id: 'workflow-approve',
    title: 'Approve Trades',
    subtitle: 'Open trades pending compliance approval',
    group: 'Workflow',
    aliases: ['APPROVE', 'APPROVALS', 'PENDING APPROVAL'],
    route: '/bond-transactions?status=PRECLEAR_PENDING',
  },
  {
    id: 'workflow-post',
    title: 'Post Executed Trades',
    subtitle: 'Open executed trades ready to post into IBOR',
    group: 'Workflow',
    aliases: ['POST', 'POST TRADES', 'POSTED', 'IBOR POST'],
    route: '/bond-transactions?status=EXECUTED',
  },
  ...ticketCommands,
];

const ticketCommandByAlias = ticketCommands.flatMap(command =>
  command.aliases.map(alias => ({ alias: normalize(alias), command }))
);

const defaultCommandIds = [
  'ticket-fi',
  'ticket-irs',
  'nav-positions',
  'nav-blotter',
  'workflow-rfq',
  'nav-compliance',
  'workflow-post',
  'nav-ai',
];

const amountPattern = /^\d+(?:\.\d+)?(?:K|M|MM|B|BN|MMM)?$/i;

const baseTicketPrefill = (rawIntent: string, tokens: string[]) => {
  const side = tokens.find(token => /^(BUY|SELL)$/i.test(token))?.toUpperCase();
  const amount = tokens.find(token => amountPattern.test(token));
  const securityParts = tokens.filter(token =>
    !/^(TKT|BUY|SELL)$/i.test(token)
    && !amountPattern.test(token)
  );

  return {
    commandIntent: rawIntent,
    transactionType: side || 'BUY',
    security: securityParts.join(' '),
    securityName: securityParts.join(' '),
    amount: amount || '',
    notionalAmount: amount || '',
    fixedNotional: amount || '',
    floatNotional: amount || '',
    submittedBy: getCurrentOperatorName(),
  };
};

const commandMatch = (
  id: string,
  title: string,
  subtitle: string,
  route: string,
  group: OperatorCommandGroup,
  score: number,
  extras: Partial<OperatorCommandDefinition> = {}
): OperatorCommandMatch => ({
  id,
  title,
  subtitle,
  group,
  aliases: extras.aliases || [],
  route,
  state: extras.state,
  sessionPrefill: extras.sessionPrefill,
  shortcut: extras.shortcut,
  example: extras.example,
  source: 'command',
  score,
});

const parseParameterizedCommand = (query: string): OperatorCommandMatch | null => {
  const trimmed = query.trim();
  if (!trimmed) return null;

  const rawTokens = trimmed.split(/\s+/).filter(Boolean);
  if (normalize(rawTokens[0] || '') === 'TKT' && rawTokens.length >= 2) {
    let ticket: OperatorCommandDefinition | undefined;
    let ticketTokenLength = 0;

    for (let length = Math.min(4, rawTokens.length - 1); length >= 1; length -= 1) {
      const alias = normalize(rawTokens.slice(1, 1 + length).join(' '));
      const match = ticketCommandByAlias.find(entry => entry.alias === alias)?.command;
      if (match) {
        ticket = match;
        ticketTokenLength = length;
        break;
      }
    }

    if (ticket) {
      const restTokens = rawTokens.slice(1 + ticketTokenLength);
      const rest = restTokens.join(' ');
      const prefill = baseTicketPrefill(trimmed, restTokens);
      return commandMatch(
        `parsed-${ticket.id}`,
        `Open ${ticket.title}`,
        rawTokens.length > 0 ? `Prefill from "${rest}"` : ticket.subtitle,
        ticket.route,
        'Trade Entry',
        120,
        {
          aliases: ['TKT'],
          sessionPrefill: prefill,
          state: { ticketPrefill: prefill, commandIntent: trimmed },
          example: ticket.example,
        }
      );
    }
  }

  const positionsMatch = trimmed.match(/^POS(?:ITIONS?)?\s+(.+)$/i);
  if (positionsMatch) {
    const value = positionsMatch[1].trim();
    return commandMatch(
      'parsed-pos-search',
      `Positions: ${value}`,
      'Open holdings filtered by security, issuer, portfolio, CUSIP, or manager',
      `/holdings?query=${encodeQuery(value)}`,
      'Search',
      115,
      { aliases: ['POS'], state: { searchQuery: value } }
    );
  }

  const rfqMatch = trimmed.match(/^RFQ\s+(.+)$/i);
  if (rfqMatch) {
    const value = rfqMatch[1].trim();
    return commandMatch(
      'parsed-rfq',
      `RFQ: ${value}`,
      'Open RFQ workflow with the entered security context',
      `/bond-transactions?action=rfq&query=${encodeQuery(value)}`,
      'Workflow',
      115,
      { aliases: ['RFQ'], state: { rfqQuery: value } }
    );
  }

  if (/^ORD(?:ERS?)?\s+TODAY$/i.test(trimmed)) {
    return commandMatch(
      'parsed-orders-today',
      'Today\'s Orders',
      'Open blotter filtered to today',
      '/bond-transactions?date=today',
      'Workflow',
      110,
      { aliases: ['ORD TODAY'] }
    );
  }

  if (/^COMP(?:LIANCE)?\s+BREACH(?:ES)?$/i.test(trimmed)) {
    return commandMatch(
      'parsed-compliance-breaches',
      'Compliance Breaches',
      'Open active compliance breaches',
      '/compliance-dashboard?tab=breaches',
      'Workflow',
      110,
      { aliases: ['COMP BREACHES'] }
    );
  }

  const cashMatch = trimmed.match(/^CASH\s+([A-Z]{3})$/i);
  if (cashMatch) {
    const currency = cashMatch[1].toUpperCase();
    return commandMatch(
      `parsed-cash-${currency}`,
      `Cash: ${currency}`,
      'Open cash management for the selected currency',
      `/middle-office?tab=cash&currency=${currency}`,
      'Workflow',
      110,
      { aliases: ['CASH'] }
    );
  }

  const abfMatch = trimmed.match(/^AB[FP]\s+(.+)$/i);
  if (abfMatch && !operatorCommands.some(command => command.aliases.some(alias => normalize(alias) === normalize(trimmed)))) {
    const value = abfMatch[1].trim();
    return commandMatch(
      'parsed-abf-search',
      `ABF: ${value}`,
      'Open the ABF portfolio filtered by facility, borrower or collateral; matching facilities and agreements are listed below',
      `/portfolio-management?tab=credit-loans&sub=abf&q=${encodeQuery(value)}`,
      'Search',
      112,
      { aliases: ['ABF'], state: { searchQuery: value } }
    );
  }

  const aiMatch = trimmed.match(/^AI\s+(.+)$/i);
  if (aiMatch) {
    const intent = aiMatch[1].trim();
    return commandMatch(
      'parsed-ai-intent',
      `Ask AI: ${intent}`,
      'Open AI Agents with the typed intent preserved',
      `/ai-agents?intent=${encodeQuery(intent)}`,
      'Workflow',
      105,
      { aliases: ['AI'], state: { aiIntent: intent } }
    );
  }

  return null;
};

export const getOperatorCommandMatches = (query: string, limit = 8): OperatorCommandMatch[] => {
  const normalizedQuery = normalize(query);

  if (!normalizedQuery) {
    return defaultCommandIds
      .map((id, index) => {
        const command = operatorCommands.find(item => item.id === id);
        if (!command) return null;
        return { ...command, source: 'command' as const, score: 90 - index };
      })
      .filter(Boolean) as OperatorCommandMatch[];
  }

  const parsed = parseParameterizedCommand(query);
  const scored = operatorCommands
    .map(command => {
      const title = normalize(command.title);
      const subtitle = normalize(command.subtitle);
      const exactAlias = command.aliases.some(alias => normalize(alias) === normalizedQuery);
      const prefixAlias = command.aliases.some(alias => normalize(alias).startsWith(normalizedQuery));
      const containsAlias = command.aliases.some(alias => normalize(alias).includes(normalizedQuery));

      let score = 0;
      if (exactAlias) score = 100;
      else if (prefixAlias) score = 80;
      else if (title.startsWith(normalizedQuery)) score = 70;
      else if (containsAlias) score = 62;
      else if (title.includes(normalizedQuery)) score = 48;
      else if (subtitle.includes(normalizedQuery)) score = 32;

      if (score === 0) return null;
      return { ...command, source: 'command' as const, score };
    })
    .filter(Boolean) as OperatorCommandMatch[];

  const matches = parsed ? [parsed, ...scored.filter(command => command.id !== parsed.id)] : scored;
  return matches
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
};

export const shortcutHelpGroups: ShortcutHelpGroup[] = [
  {
    title: 'Global',
    items: [
      { keys: ['Cmd/Ctrl', 'K'], action: 'Open command palette' },
      { keys: ['?'], action: 'Show keyboard shortcuts' },
      { keys: ['Esc'], action: 'Close palette, drawer, or overlay' },
      { keys: ['/'], action: 'Focus page search', note: 'Falls back to command palette if the page has no search box' },
      { keys: ['Cmd/Ctrl', '/'], action: 'Open AI Agents' },
    ],
  },
  {
    title: 'Navigate',
    items: [
      { keys: ['g', 'd'], action: 'Dashboard' },
      { keys: ['g', 'p'], action: 'Positions / holdings' },
      { keys: ['g', 'b'], action: 'Order blotter' },
      { keys: ['g', 't'], action: 'Trade entry' },
      { keys: ['g', 'c'], action: 'Compliance' },
      { keys: ['g', 'r'], action: 'Analytics / reports' },
      { keys: ['g', 'a'], action: 'AI Agents' },
      { keys: ['g', 'm'], action: 'Document processing' },
      { keys: ['g', 'l'], action: 'Credit & Loans' },
      { keys: ['g', 'v'], action: 'Alternatives' },
      { keys: ['g', 's'], action: 'Settings' },
    ],
  },
  {
    title: 'New Ticket',
    items: [
      { keys: ['n', 't'], action: 'New default ticket' },
      { keys: ['n', 'f'], action: 'New fixed income ticket' },
      { keys: ['n', 'e'], action: 'New equity ticket' },
      { keys: ['n', 'l'], action: 'New leveraged loan ticket' },
      { keys: ['n', 'p'], action: 'New private credit ticket' },
      { keys: ['n', 'i'], action: 'New interest rate swap ticket' },
      { keys: ['n', 'x'], action: 'New FX ticket' },
      { keys: ['n', 'r'], action: 'New repo ticket' },
    ],
  },
  {
    title: 'Ticket Workflow',
    items: [
      { keys: ['Cmd/Ctrl', 'S'], action: 'Save or update draft' },
      { keys: ['Cmd/Ctrl', 'Enter'], action: 'Run the next enabled ticket action' },
      { keys: ['Cmd/Ctrl', 'Shift', 'P'], action: 'Submit for pre-clear', note: 'Alt/Option + Shift + P also works' },
      { keys: ['Cmd/Ctrl', 'Shift', 'A'], action: 'Approve pre-clear', note: 'Role-gated. Alt/Option + Shift + A also works' },
      { keys: ['Cmd/Ctrl', 'Shift', 'R'], action: 'Release approved trade', note: 'Alt/Option + Shift + R avoids browser hard-refresh conflicts' },
      { keys: ['Cmd/Ctrl', 'Shift', 'E'], action: 'Execute sent trade', note: 'Alt/Option + Shift + E also works' },
      { keys: ['Cmd/Ctrl', 'Shift', 'O'], action: 'Open executed trades ready for IBOR posting', note: 'Alt/Option + Shift + O also works' },
      { keys: ['Cmd/Ctrl', 'D'], action: 'Duplicate ticket', note: 'Alt/Option + Shift + D also works' },
      { keys: ['Cmd/Ctrl', 'J'], action: 'Jump to validation errors', note: 'Alt/Option + Shift + J also works' },
      { keys: ['Cmd/Ctrl', 'L'], action: 'Toggle allocations section', note: 'Alt/Option + Shift + L also works' },
    ],
  },
  {
    title: 'Mnemonic Examples',
    items: [
      { keys: ['FI'], action: 'Open fixed income ticket' },
      { keys: ['IRS'], action: 'Open interest rate swap ticket' },
      { keys: ['CDS'], action: 'Open credit default swap ticket' },
      { keys: ['POS VANGUARD'], action: 'Search positions' },
      { keys: ['RFQ IBM 2034'], action: 'Start RFQ context' },
      { keys: ['TKT FI IBM 5MM BUY'], action: 'Open and prefill a fixed income draft' },
      { keys: ['COMP BREACHES'], action: 'Open compliance breaches' },
    ],
  },
];
